import type { ReactNode } from "react";

export interface RecentChipsProps<T> {
  /** Label above the chips, such as "Recent plans" or "Frequently used". */
  label: string;
  items: T[];
  getKey: (item: T) => string;
  /** Chip body; keep it to one truncatable line. */
  renderChip: (item: T) => ReactNode;
  /** Accessible chip name (the visible body is decorative markup). */
  getChipLabel: (item: T) => string;
  onPick: (item: T) => void;
  /** Wipes both recents lists (plans and drugs), because pharmacy terminals are shared. */
  onClear: () => void;
}

/**
 * One-tap quick-pick chips for the cost tool's remembered plans and drugs. The
 * partner works with the same handful all day, so a tap replaces a whole
 * type-search-click round trip. Renders nothing when `items` is empty.
 * "Clear recents" sits with the chips, so a shared terminal can be wiped
 * wherever the recents are visible.
 */
export function RecentChips<T>({
  label,
  items,
  getKey,
  renderChip,
  getChipLabel,
  onPick,
  onClear,
}: RecentChipsProps<T>): ReactNode {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
          {label}
        </p>
        <button
          type="button"
          onClick={onClear}
          className="text-slate-teal hover:text-foreground cursor-pointer text-xs underline underline-offset-2"
        >
          Clear recents
        </button>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {items.map((item) => (
          <button
            key={getKey(item)}
            type="button"
            aria-label={getChipLabel(item)}
            onClick={() => onPick(item)}
            className="border-haze bg-ice text-foreground hover:border-ember max-w-full cursor-pointer rounded-lg border px-3 py-1.5 text-sm"
          >
            {renderChip(item)}
          </button>
        ))}
      </div>
    </div>
  );
}
